import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { reapply, updateApplicationCurrentFlow } from 'thunks';
import ActionCard from 'components/Common/Cards/ActionCard';
import ProbableOfferMark from 'components/FinancialCheckup/ProbableOfferMark/ProbableOfferMark';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getAuthData } from 'selectors/getAuthData';
import { getYourNameData } from 'selectors/yourName';
import { getReapplyData } from 'selectors/reapplyData';
import { setIsReapplying } from 'handlers/reapplyData';
import { getAuthOtpBorrowerFields } from 'handlers/yourName';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { CurrentFlow } from 'enums/CurrentFlow';
import useOpenAiCoachConversationChat from 'hooks/useOpenAiCoachConversationChat';
import usePursueLoanOffer from 'hooks/usePursueLoanOffer';
import { hasPrimaryGoalCompleted, hasSelectedAICoachGoals } from 'utils/aiCoachUtils';
import { FROM_FINANCIAL_DASHBOARD_STATE, OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';

import { mapAppStatusToActionCardProps } from './resolveNextStepActionCard';

import styles from './NextStepSection.module.scss';

const NextStepSection: React.FC = () => {
  const { application } = useSelector(getApplicationData);
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const { sessionToken } = useSelector(getAuthData);
  const yourNameData = useSelector(getYourNameData);
  const { data: reapplyData } = useSelector(getReapplyData);
  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const openAiCoachConversationChat = useOpenAiCoachConversationChat();
  const { pursueLoan, isPursuingLoan } = usePursueLoanOffer();

  if (!application) return null;

  const nextStepCardProps = mapAppStatusToActionCardProps(application, aiCoachGoals || []);

  if (!nextStepCardProps) return null;

  const {
    title,
    description,
    buttonText,
    estimatedDuration,
    route,
    chip,
    isLoading,
    isProbableOffer,
  } = nextStepCardProps;

  const handleNextStepClick = async () => {
    if (isProbableOffer) {
      await pursueLoan();
      return;
    }

    if (route === RoutePath.AICoachGoals) {
      if (hasSelectedAICoachGoals(aiCoachGoals) && !hasPrimaryGoalCompleted(aiCoachGoals)) {
        openAiCoachConversationChat();
        return;
      }

      if (application.currentFlow !== CurrentFlow.AICoach) {
        setIsLoadingNextStep(true);
        try {
          await dispatchWithUnwrap(
            updateApplicationCurrentFlow({
              applicationId: application.id,
              currentFlow: CurrentFlow.AICoach,
            }),
          );
        } finally {
          setIsLoadingNextStep(false);
        }
      }

      navigate(RoutePath.AICoachGoals, FROM_FINANCIAL_DASHBOARD_STATE);
      return;
    }

    if (route === RoutePath.ConfirmReapplyDetails) {
      if (!reapplyData) {
        const borrowerFields = getAuthOtpBorrowerFields(yourNameData);
        const firstName = borrowerFields.firstName || application.borrowerFirstName;
        const lastName = borrowerFields.lastName || application.borrowerLastName;
        const credential = borrowerFields.credential || application.borrowerCredentials;

        if (!sessionToken || !firstName || !lastName) return;

        setIsLoadingNextStep(true);
        try {
          await dispatchWithUnwrap(
            reapply({
              sessionToken,
              firstName,
              lastName,
              ...(credential && { credential }),
            }),
          );
        } catch {
          setIsLoadingNextStep(false);
          return;
        }
        setIsLoadingNextStep(false);
      }

      dispatch(setIsReapplying(true));
      navigate(RoutePath.ConfirmReapplyDetails);
      return;
    }

    if (route) navigate(route, route === RoutePath.OfferAvailable ? OFFER_AVAILABLE_FROM_DASHBOARD_STATE : undefined);
  };

  return (
    <div className={styles.nextStepSection}>
      <p className={styles.sectionLabel}>Next step</p>
      <ActionCard>
        {chip}
        <div>
          {estimatedDuration && <ActionCard.Duration>{estimatedDuration}</ActionCard.Duration>}
          <ActionCard.Title>{title}</ActionCard.Title>
          {description && <ActionCard.Description>{description}</ActionCard.Description>}
        </div>
        {buttonText && (
          <ActionCard.Button onClick={handleNextStepClick} isLoading={isLoading || isLoadingNextStep || isPursuingLoan}>
            {buttonText}
            {isProbableOffer && <ProbableOfferMark />}
          </ActionCard.Button>
        )}
      </ActionCard>
    </div>
  );
};

export default NextStepSection;
